import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';
import { Users, Search, Upload, History, Ban, RotateCcw, CheckCircle } from 'lucide-react';
import ProgrammeEnrollmentsAdmin from './ProgrammeEnrollmentsAdmin';

const API = `${API_BASE}/api`;

const STATUS_STYLES = {
  active: 'bg-green-500/15 text-green-400',
  in_progress: 'bg-green-500/15 text-green-400',
  completed: 'bg-teal-500/15 text-teal-300',
  failed: 'bg-orange-500/15 text-orange-300',
  revoked: 'bg-red-500/15 text-red-400',
};

export default function EnrollmentsAdmin({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [view, setView] = useState('list'); // list | enroll | csv | audit
  const qs = new URLSearchParams(window.location.search);
  const [mode, setMode] = useState(qs.get('mode') === 'courses' ? 'courses' : 'programmes');
  const initialProgramme = qs.get('programme') || '';

  // ---- list state ----
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // ---- shared lookups ----
  const [allUsers, setAllUsers] = useState([]);
  const [allCourses, setAllCourses] = useState([]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (courseFilter) params.append('course_id', courseFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await axios.get(`${API}/admin/enrollments?${params.toString()}`, { headers });
      setRows(res.data.enrollments || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Failed to load enrollments');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, courseFilter, statusFilter, dateFrom, dateTo]);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    const load = async () => {
      try {
        const [u, c] = await Promise.all([
          axios.get(`${API}/admin/users?limit=5000`, { headers }).catch(() => ({ data: { users: [] } })),
          axios.get(`${API}/courses`, { headers }).catch(() => ({ data: [] })),
        ]);
        setAllUsers(u.data.users || []);
        setAllCourses(Array.isArray(c.data) ? c.data : (c.data.courses || []));
      } catch (e) { /* noop */ }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const revoke = async (id) => {
    if (!window.confirm('Revoke this enrollment? The learner will immediately lose access (progress is kept).')) return;
    try {
      await axios.post(`${API}/admin/enrollments/${id}/revoke`, {}, { headers });
      toast.success('Enrollment revoked');
      fetchList();
    } catch (e) { toast.error('Failed to revoke'); }
  };

  const reenroll = async (id) => {
    try {
      await axios.post(`${API}/admin/enrollments/${id}/reenroll`, {}, { headers });
      toast.success('Enrollment restored');
      fetchList();
    } catch (e) { toast.error('Failed to re-enroll'); }
  };

  const tabBtn = (id, label, Icon) => (
    <button
      onClick={() => setView(id)}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${view === id ? 'bg-navigate-gold text-black' : 'bg-navigate-surface text-zinc-300 hover:text-white border border-zinc-800'}`}
      data-testid={`enrollments-tab-${id}`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl font-bold text-white">Enrollments</h1>
      </div>

      <div className="inline-flex rounded-lg border border-zinc-800 p-1 mb-6" data-testid="enrollments-mode-switch">
        {[['programmes', 'Programmes'], ['courses', 'Courses']].map(([id, label]) => (
          <button key={id} onClick={() => setMode(id)} data-testid={`enrollments-mode-${id}`}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${mode === id ? 'bg-navigate-gold text-black' : 'text-zinc-400 hover:text-white'}`}>
            {label}
          </button>
        ))}
      </div>

      {mode === 'programmes' ? (
        <ProgrammeEnrollmentsAdmin headers={headers} allUsers={allUsers} initialProgramme={initialProgramme} />
      ) : (<>
      <div className="flex flex-wrap gap-3 mb-6">
        {tabBtn('list', 'All Enrollments', Users)}
        {tabBtn('enroll', 'Enroll Users', CheckCircle)}
        {tabBtn('csv', 'Bulk CSV', Upload)}
        {tabBtn('audit', 'Audit Log', History)}
      </div>

      {view === 'list' && (
        <div>
          {/* Filters */}
          <div className="grid md:grid-cols-5 gap-3 mb-4">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <input
                className="w-full pl-9 h-9 bg-navigate-surface border border-zinc-800 rounded-md text-sm text-white px-2"
                placeholder="Search user or course..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="enrollment-search"
              />
            </div>
            <select className="h-9 bg-navigate-surface border border-zinc-800 rounded-md text-sm text-white px-2" value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)}>
              <option value="">All courses</option>
              {allCourses.map((c) => <option key={c.course_id} value={c.course_id}>{c.title}</option>)}
            </select>
            <select className="h-9 bg-navigate-surface border border-zinc-800 rounded-md text-sm text-white px-2" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="revoked">Revoked</option>
              <option value="failed">Failed</option>
            </select>
            <div className="flex gap-2">
              <input type="date" className="h-9 w-1/2 bg-navigate-surface border border-zinc-800 rounded-md text-xs text-white px-2" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} title="From" />
              <input type="date" className="h-9 w-1/2 bg-navigate-surface border border-zinc-800 rounded-md text-xs text-white px-2" value={dateTo} onChange={(e) => setDateTo(e.target.value)} title="To" />
            </div>
          </div>

          <p className="text-xs text-zinc-500 mb-2">{total} enrollment{total === 1 ? '' : 's'}</p>

          <div className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-zinc-800/50">
                <tr>
                  {['User', 'Email', 'Course', 'Enrolled', 'Enrolled by', 'Progress', 'Status', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {loading ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-zinc-500">Loading...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-zinc-500">No enrollments found</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.enrollment_id} className="hover:bg-zinc-800/30" data-testid={`enrollment-row-${r.enrollment_id}`}>
                    <td className="px-4 py-3 text-sm text-white">{r.user_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-zinc-400">{r.user_email || '—'}</td>
                    <td className="px-4 py-3 text-sm text-zinc-300">{r.course_title || r.course_id}</td>
                    <td className="px-4 py-3 text-xs text-zinc-500">{(r.enrolled_at || '').slice(0, 10)}</td>
                    <td className="px-4 py-3 text-xs text-zinc-500">{r.enrolled_by || '—'}</td>
                    <td className="px-4 py-3 text-sm text-zinc-300">{Math.round(r.progress_percent || 0)}%</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[r.status] || 'bg-zinc-700 text-zinc-300'}`}>{r.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      {r.status === 'revoked' ? (
                        <button onClick={() => reenroll(r.enrollment_id)} className="flex items-center gap-1 text-xs text-green-400 hover:text-green-300" data-testid={`reenroll-${r.enrollment_id}`}>
                          <RotateCcw className="h-3.5 w-3.5" /> Re-enroll
                        </button>
                      ) : (
                        <button onClick={() => revoke(r.enrollment_id)} className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300" data-testid={`revoke-${r.enrollment_id}`}>
                          <Ban className="h-3.5 w-3.5" /> Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === 'enroll' && (
        <EnrollUsersPanel headers={headers} allUsers={allUsers} allCourses={allCourses} onDone={() => { setView('list'); fetchList(); }} />
      )}

      {view === 'csv' && (
        <CsvPanel headers={headers} onDone={() => { setView('list'); fetchList(); }} />
      )}

      {view === 'audit' && (
        <AuditPanel headers={headers} />
      )}
      </>)}
    </div>
  );
}

// =====================================================================
function EnrollUsersPanel({ headers, allUsers, allCourses, onDone }) {
  const [selUsers, setSelUsers] = useState([]);
  const [selCourses, setSelCourses] = useState([]);
  const [userQuery, setUserQuery] = useState('');
  const [courseQuery, setCourseQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const toggle = (arr, setArr, id) => setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const submit = async () => {
    if (!selUsers.length || !selCourses.length) { toast.error('Select at least one user and one course'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/enrollments/enroll`, { user_ids: selUsers, course_ids: selCourses }, { headers });
      const d = res.data;
      toast.success(`Enrolled ${d.enrolled}, re-enrolled ${d.reenrolled}, skipped ${d.already_enrolled}`);
      if (d.errors?.length) toast.error(`${d.errors.length} error(s)`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to enroll');
    } finally { setSubmitting(false); }
  };

  const fUsers = allUsers.filter((u) => `${u.full_name} ${u.email}`.toLowerCase().includes(userQuery.toLowerCase()));
  const fCourses = allCourses.filter((c) => (c.title || '').toLowerCase().includes(courseQuery.toLowerCase()));

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-white mb-3">Select Users ({selUsers.length})</h3>
        <input className="w-full h-9 bg-navigate-dark border border-zinc-700 rounded-md text-sm text-white px-2 mb-3" placeholder="Search users..." value={userQuery} onChange={(e) => setUserQuery(e.target.value)} />
        <div className="max-h-80 overflow-y-auto space-y-1">
          {fUsers.map((u) => (
            <label key={u.user_id} className="flex items-center gap-2 p-2 rounded hover:bg-zinc-800/40 cursor-pointer text-sm text-zinc-300">
              <input type="checkbox" checked={selUsers.includes(u.user_id)} onChange={() => toggle(selUsers, setSelUsers, u.user_id)} data-testid={`enroll-user-${u.email}`} />
              <span>{u.full_name} <span className="text-zinc-500">({u.email})</span></span>
            </label>
          ))}
          {fUsers.length === 0 && <p className="text-xs text-zinc-500 p-2">No users</p>}
        </div>
      </div>

      <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-white mb-3">Select Courses ({selCourses.length})</h3>
        <input className="w-full h-9 bg-navigate-dark border border-zinc-700 rounded-md text-sm text-white px-2 mb-3" placeholder="Search courses..." value={courseQuery} onChange={(e) => setCourseQuery(e.target.value)} />
        <div className="max-h-80 overflow-y-auto space-y-1">
          {fCourses.map((c) => (
            <label key={c.course_id} className="flex items-center gap-2 p-2 rounded hover:bg-zinc-800/40 cursor-pointer text-sm text-zinc-300">
              <input type="checkbox" checked={selCourses.includes(c.course_id)} onChange={() => toggle(selCourses, setSelCourses, c.course_id)} data-testid={`enroll-course-${c.course_id}`} />
              <span>{c.title}</span>
            </label>
          ))}
          {fCourses.length === 0 && <p className="text-xs text-zinc-500 p-2">No courses</p>}
        </div>
      </div>

      <div className="md:col-span-2">
        <button onClick={submit} disabled={submitting} className="btn-gold px-6 py-2.5 text-sm disabled:opacity-50" data-testid="confirm-enroll-btn">
          {submitting ? 'Enrolling...' : `Enroll ${selUsers.length} user(s) in ${selCourses.length} course(s)`}
        </button>
      </div>
    </div>
  );
}

// =====================================================================
function CsvPanel({ headers, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const doPreview = async () => {
    if (!file) { toast.error('Choose a CSV file'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/enrollments/csv-preview`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to parse CSV');
    } finally { setBusy(false); }
  };

  const doCommit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/enrollments/csv-commit`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      const d = res.data;
      toast.success(`Enrolled ${d.enrolled}, re-enrolled ${d.reenrolled}, skipped ${d.already_enrolled}, errors ${d.errors?.length || 0}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to commit CSV');
    } finally { setBusy(false); }
  };

  const rowColor = (s) => ({
    ok: 'text-green-400', revoked_will_reenroll: 'text-teal-300',
    already_enrolled: 'text-amber-400', unknown_user: 'text-red-400',
    unknown_course: 'text-red-400', error: 'text-red-400',
  }[s] || 'text-zinc-300');

  return (
    <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
      <p className="text-sm text-zinc-400 mb-4">Upload a CSV with columns <code className="bg-zinc-800 px-1.5 py-0.5 rounded">email</code> and <code className="bg-zinc-800 px-1.5 py-0.5 rounded">course</code> (course can be a title, slug or course_id).</p>
      <div className="flex items-center gap-3 mb-4">
        <input type="file" accept=".csv" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} className="text-sm text-zinc-300" data-testid="csv-file-input" />
        <button onClick={doPreview} disabled={busy} className="px-4 py-2 rounded-lg border border-zinc-700 text-zinc-200 text-sm disabled:opacity-50">Preview</button>
      </div>

      {preview && (
        <div>
          <p className="text-xs text-zinc-500 mb-2">{preview.valid} of {preview.total} rows will be enrolled</p>
          <div className="max-h-80 overflow-y-auto border border-zinc-800 rounded-lg mb-4">
            <table className="w-full text-sm">
              <thead className="bg-zinc-800/50 sticky top-0"><tr>
                <th className="px-3 py-2 text-left text-xs text-zinc-400">Row</th>
                <th className="px-3 py-2 text-left text-xs text-zinc-400">Email</th>
                <th className="px-3 py-2 text-left text-xs text-zinc-400">Course</th>
                <th className="px-3 py-2 text-left text-xs text-zinc-400">Result</th>
              </tr></thead>
              <tbody className="divide-y divide-zinc-800">
                {preview.rows.map((r) => (
                  <tr key={r.row}>
                    <td className="px-3 py-2 text-zinc-500">{r.row}</td>
                    <td className="px-3 py-2 text-zinc-300">{r.email}</td>
                    <td className="px-3 py-2 text-zinc-300">{r.course_title || r.course}</td>
                    <td className={`px-3 py-2 ${rowColor(r.status)}`}>{r.status}{r.message ? ` — ${r.message}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button onClick={doCommit} disabled={busy || preview.valid === 0} className="btn-gold px-6 py-2.5 text-sm disabled:opacity-50" data-testid="csv-commit-btn">
            {busy ? 'Working...' : `Confirm & enroll ${preview.valid} row(s)`}
          </button>
        </div>
      )}
    </div>
  );
}

// =====================================================================
function AuditPanel({ headers }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/admin/enrollments/audit`, { headers });
        setEntries(res.data.entries || []);
      } catch (e) { toast.error('Failed to load audit log'); }
      finally { setLoading(false); }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-x-auto">
      <table className="w-full min-w-[700px]">
        <thead className="bg-zinc-800/50"><tr>
          {['Action', 'User', 'Course', 'Admin', 'When'].map((h) => (
            <th key={h} className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">{h}</th>
          ))}
        </tr></thead>
        <tbody className="divide-y divide-zinc-800">
          {loading ? (
            <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-500">Loading...</td></tr>
          ) : entries.length === 0 ? (
            <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-500">No audit entries yet</td></tr>
          ) : entries.map((e) => (
            <tr key={e.audit_id} className="hover:bg-zinc-800/30">
              <td className="px-4 py-3"><span className="px-2 py-1 rounded-full text-xs bg-zinc-700 text-zinc-200">{e.action}</span></td>
              <td className="px-4 py-3 text-sm text-zinc-300">{e.user_id}</td>
              <td className="px-4 py-3 text-sm text-zinc-300">{e.course_id}</td>
              <td className="px-4 py-3 text-sm text-zinc-400">{e.admin_email}</td>
              <td className="px-4 py-3 text-xs text-zinc-500">{(e.timestamp || '').replace('T', ' ').slice(0, 19)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
